"use client";

import { useSyncExternalStore } from "react";

/**
 * useIdle — the one idle signal (audit 09-24: the Dock ball's breath and
 * blink, and the task page's face, animated forever on every page).
 *
 * Idle after IDLE_AFTER_MS with no pointer, key, wheel, touch or scroll, and
 * at once while the tab is hidden. Any of those, or the tab coming back, wakes
 * it at once. One set of listeners however many surfaces read it; an input
 * only stamps a time, and the one timer re-arms itself for what is left.
 *
 * It says only that nobody is at the screen. A surface rests on it only when
 * nothing is happening there too (a call, a voice, a task at work), and rests
 * by pausing its animations, never by removing them, so waking has no jump.
 */
export const IDLE_AFTER_MS = 30_000;

const WAKE_EVENTS = [
  "pointermove",
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
  "scroll",
] as const;
/** Capture, so a scroll inside any scroller reaches the window. */
const LISTEN = { capture: true, passive: true } as const;

let idle = false;
let lastActive = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

const hidden = () => document.visibilityState === "hidden";

function set(next: boolean) {
  if (idle === next) return;
  idle = next;
  listeners.forEach((l) => l());
}

function arm(ms: number) {
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(check, ms);
}

function check() {
  timer = null;
  if (hidden()) return set(true);
  const quiet = Date.now() - lastActive;
  if (quiet >= IDLE_AFTER_MS) set(true);
  else arm(IDLE_AFTER_MS - quiet);
}

function onActive() {
  lastActive = Date.now();
  if (idle) set(false);
  if (timer === null) arm(IDLE_AFTER_MS);
}

function onVisibility() {
  if (!hidden()) return onActive();
  if (timer !== null) clearTimeout(timer);
  timer = null;
  set(true);
}

function start() {
  WAKE_EVENTS.forEach((e) => window.addEventListener(e, onActive, LISTEN));
  document.addEventListener("visibilitychange", onVisibility);
  lastActive = Date.now();
  idle = hidden();
  if (!idle) arm(IDLE_AFTER_MS);
}

function stop() {
  WAKE_EVENTS.forEach((e) => window.removeEventListener(e, onActive, LISTEN));
  document.removeEventListener("visibilitychange", onVisibility);
  if (timer !== null) clearTimeout(timer);
  timer = null;
  idle = false;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stop();
  };
}

export function useIdle(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => idle,
    () => false
  );
}
